// DiscoveryBurst (§5.1) — tier-colored particle burst + rotating light rays +
// pulsing glow rendered behind the discovered orb. Ported from the prototype's
// burst() generator. Transform/opacity animations only (never gates on opacity:0
// entrance) so it can't freeze invisible on a slow load.
import React, { useMemo } from "react";

interface DiscoveryBurstProps {
  color: string;
}

const DiscoveryBurst: React.FC<DiscoveryBurstProps> = ({ color }) => {
  // Randomised once per mount so the burst is stable while visible.
  const particles = useMemo(
    () =>
      Array.from({ length: 18 }, (_, i) => {
        const ang = (Math.PI * 2 * i) / 18 + (Math.random() - 0.5) * 0.4;
        const dist = 46 + Math.random() * 26;
        const sz = 5 + Math.random() * 5;
        return {
          i,
          dx: Math.cos(ang) * dist,
          dy: Math.sin(ang) * dist,
          sz,
          delay: Math.random() * 0.12,
        };
      }),
    []
  );

  const palette = [color, "#F5B740", "#FFFFFF", "var(--accent-text)"];

  return (
    <div style={{ position: "absolute", left: 0, right: 0, top: 0, height: 0, zIndex: 0, pointerEvents: "none" }}>
      <span
        style={{
          position: "absolute",
          left: "50%",
          top: 96,
          width: 132,
          height: 132,
          transform: "translate(-50%,-50%)",
          borderRadius: "50%",
          background: `radial-gradient(circle,${color}55,transparent 68%)`,
          animation: "gGlow 1.7s ease-in-out infinite",
        }}
      />
      <span
        style={{
          position: "absolute",
          left: "50%",
          top: 96,
          width: 150,
          height: 150,
          transform: "translate(-50%,-50%)",
          borderRadius: "50%",
          background: `conic-gradient(from 0deg,${color}00,${color}66,${color}00,${color}66,${color}00,${color}66,${color}00,${color}66,${color}00)`,
          opacity: 0.45,
          animation: "gRays 3.2s linear infinite",
        }}
      />
      {particles.map((p) => (
        <span
          key={p.i}
          style={{
            position: "absolute",
            left: "50%",
            top: 96,
            width: p.sz,
            height: p.sz,
            borderRadius: p.i % 3 ? "50%" : 2,
            background: palette[p.i % palette.length],
            ["--tx" as any]: `${p.dx.toFixed(0)}px`,
            ["--ty" as any]: `${p.dy.toFixed(0)}px`,
            animation: "gBurst .95s cubic-bezier(.2,.7,.3,1) forwards",
            animationDelay: `${p.delay.toFixed(2)}s`,
          }}
        />
      ))}
    </div>
  );
};

export default DiscoveryBurst;
